import { useContext } from "react";
import { Tooltip, Divider, Text, Indicator } from "@mantine/core";
import { NavLink, useLocation } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { UploadContext } from "../contexts/UploadContext";
import {
  IconLayoutDashboard,
  IconLayoutDashboardFilled,
  IconClipboardList,
  IconClipboardListFilled,
  IconStack2,
  IconStack2Filled,
  IconUpload,
  IconBuilding,
  IconUsers,
} from "@tabler/icons-react";
import { UserLevels } from "../utils/constants";
import classes from "../styles/Sidebar.module.css";
import pkg from "../../package.json";

const ICON_SIZE = 18;

/** True when pathname is `base` or sits underneath it, e.g. /users/12 under /users. */
function isUnder(pathname, base) {
  return pathname === base || pathname.startsWith(`${base}/`);
}

export default function Sidebar({ opened }) {
  const { user, workingInIsAssetOwner } = useContext(AuthContext);
  const { uploadingCount } = useContext(UploadContext);
  const { pathname } = useLocation();

  const links = [
    {
      to: "/dashboard",
      label: "Dashboard",
      Icon: IconLayoutDashboard,
      ActiveIcon: IconLayoutDashboardFilled,
      // Staff get the dashboard too (with the org / All Organisations toggle).
      show: user?.userLevel !== UserLevels.UPLOAD_ONLY,
    },
    {
      to: "/inspections",
      label: "Inspections",
      Icon: IconClipboardList,
      ActiveIcon: IconClipboardListFilled,
      alsoMatch: ["/inspection"],
      // Staff see inspections too - data is scoped by the "Working In" org
      // selector in the header, giving them a global, switchable view.
      show: user?.userLevel !== UserLevels.UPLOAD_ONLY,
    },
    {
      to: "/assets",
      label: "Assets",
      Icon: IconStack2,
      ActiveIcon: IconStack2Filled,
      alsoMatch: ["/asset"],
      // Staff always get Assets. Everyone else only when Working As is an asset
      // owner. Switching Working In to a contractor's linked asset owner does
      // not reveal it.
      show:
        user?.userLevel !== UserLevels.UPLOAD_ONLY &&
        (user?.isStaff || workingInIsAssetOwner),
    },
    {
      to: "/uploads",
      label: "Uploads",
      Icon: IconUpload,
      ActiveIcon: IconUpload,
      indicator: uploadingCount > 0 ? uploadingCount : undefined,
      show: true,
    },
    {
      to: "/organisations",
      label: "Organisations",
      Icon: IconBuilding,
      ActiveIcon: IconBuilding,
      alsoMatch: ["/organisation"],
      show: user?.isStaff,
    },
    {
      to: "/users",
      label: "Users",
      Icon: IconUsers,
      ActiveIcon: IconUsers,
      show: user?.isStaff,
    },
  ];

  return (
    <div className={classes.nav}>
      <div className={classes.links}>
        {links
          .filter((l) => l.show)
          .map(({ to, label, Icon, ActiveIcon, indicator, alsoMatch }) => {
            const isActive = [to, ...(alsoMatch ?? [])].some((base) =>
              isUnder(pathname, base)
            );
            return (
              <Tooltip
                label={label}
                position="right"
                withArrow
                disabled={opened}
                openDelay={500}
                key={to}
              >
                <NavLink
                  to={to}
                  data-active={isActive || undefined}
                  data-testid={`sidebar-nav-${label.toLowerCase()}`}
                  className={`${classes.link} ${opened ? classes.expanded : classes.collapsed}`}
                >
                  <Indicator
                    disabled={!indicator}
                    processing
                    label={
                      <Text size="10px" fw={600}>
                        {indicator}
                      </Text>
                    }
                    size={16}
                    offset={2}
                    position="top-end"
                    className={classes.icon}
                  >
                    {isActive ? (
                      <ActiveIcon size={ICON_SIZE} stroke={1.6} />
                    ) : (
                      <Icon size={ICON_SIZE} stroke={1.6} />
                    )}
                  </Indicator>
                  {opened && (
                    <Text
                      size="13px"
                      fw={isActive ? 600 : 400}
                      className={classes.label}
                    >
                      {label}
                    </Text>
                  )}
                </NavLink>
              </Tooltip>
            );
          })}
      </div>

      {opened && (
        <div className={classes.footer}>
          <Divider mb="xs" w="100%" />
          <Text size="10px" c="dimmed">
            © 2026 VAPAR Innovation Pty Ltd
          </Text>
          <Text size="10px" c="dimmed">
            v{pkg.version}
          </Text>
        </div>
      )}
    </div>
  );
}
